import { useCallback } from "react"
import {
  ACTIVE_JOB_STATUSES,
  useLazyGetJobQuery,
  type JobRunView,
} from "~/ui-rtk/api/jobsApi"

export interface WaitForJobOptions {
  /** Poll spacing in ms (default 3s). */
  intervalMs?: number
  /** Give up after this long (default 10 min) — rejects with a timeout error. */
  timeoutMs?: number
  /** Abort the wait early (drawer closed, selection changed) — rejects with an AbortError. */
  signal?: AbortSignal
}

const DEFAULT_INTERVAL_MS = 3_000
const DEFAULT_TIMEOUT_MS = 10 * 60 * 1_000
/** Consecutive failed polls tolerated before giving up (transient network blips). */
const MAX_CONSECUTIVE_POLL_ERRORS = 3

const abortError = () => new DOMException("Job wait aborted", "AbortError")

const sleep = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(abortError())
      return
    }
    const handle: { timer?: ReturnType<typeof setTimeout> } = {}
    const onAbort = () => {
      clearTimeout(handle.timer)
      reject(abortError())
    }
    handle.timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort)
      resolve()
    }, ms)
    signal?.addEventListener("abort", onAbort, { once: true })
  })

export const isAbortError = (error: unknown): boolean =>
  error instanceof DOMException && error.name === "AbortError"

/**
 * Imperative "enqueue, then await the result" helper for endpoints that
 * return 202 `{ jobRunId }` (anything that outlives the 29s API Gateway
 * limit — Gemini video analysis, blasts…). Polls GET /jobs/:id until the job
 * leaves an active status and resolves with the terminal `JobRunView`; the
 * caller reads `status` / `result` / `error` from it. Never resolves while
 * the job is still running, so the returned view is final.
 *
 *   const { jobRunId } = await analyzeMedia(args).unwrap()
 *   const job = await waitForJob(jobRunId, { signal })
 *   if (job.status !== "COMPLETED") throw new Error(job.error ?? "…")
 */
export function useWaitForJob() {
  const [getJob] = useLazyGetJobQuery()

  return useCallback(
    async (
      jobRunId: string,
      {
        intervalMs = DEFAULT_INTERVAL_MS,
        timeoutMs = DEFAULT_TIMEOUT_MS,
        signal,
      }: WaitForJobOptions = {},
    ): Promise<JobRunView> => {
      const startedAt = Date.now()
      let consecutiveErrors = 0
      for (;;) {
        if (signal?.aborted) throw abortError()
        try {
          // preferCacheValue=false: always hit the server — a cached view
          // from a previous poll is by definition stale here.
          const job = await getJob(jobRunId, false).unwrap()
          consecutiveErrors = 0
          if (!ACTIVE_JOB_STATUSES.includes(job.status)) {
            return job
          }
        } catch (error) {
          consecutiveErrors += 1
          if (consecutiveErrors >= MAX_CONSECUTIVE_POLL_ERRORS) {
            throw error
          }
        }
        if (Date.now() - startedAt > timeoutMs) {
          throw new Error("Timed out waiting for the job to finish.")
        }
        await sleep(intervalMs, signal)
      }
    },
    [getJob],
  )
}
